import { useState } from "react";
import {
  Dialog,
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  Avatar,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { colors } from "../source/source.styles";
import { X, Send, FileText } from "lucide-react";

const MessageAgentModal = ({ open, onClose, employee }) => {
  const theme = useTheme();
  const [message, setMessage] = useState("");
  const maxLength = 2000;

  const handleSendMessage = () => {
    if (message.trim()) {
      console.log("Sending message to:", employee?.name, message);
      setMessage("");
      onClose();
    }
  };

  const handleLoadTemplateFromScratch = () => {
    console.log("Load template from scratch");
  };

  const handleLoadTemplateFromFile = () => {
    console.log("Load template from file");
  };

  const getAvatarColor = () => {
    return theme.palette.primary.main;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Avatar
              sx={{
                width: 40,
                height: 40,
                bgcolor: getAvatarColor(),
                color: "#fff",
                fontSize: 16,
                fontWeight: 600,
              }}
            >
              {employee?.name
                ?.split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase() || "SJ"}
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                Message {employee?.name || "Sarah Johnson"}
              </Typography>
            </Box>
          </Stack>
          <IconButton
            onClick={onClose}
            sx={{
              color: colors.textMuted,
              "&:hover": {
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            <X size={20} />
          </IconButton>
        </Stack>

        {/* Conversation History (Optional - showing previous messages) */}
        <Box
          sx={{
            mb: 2,
            maxHeight: 200,
            overflowY: "auto",
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              bgcolor: colors.bgPrimary,
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: colors.border,
              borderRadius: "3px",
            },
          }}
        >
          {/* Example previous message */}
          <Box
            sx={{
              mb: 2,
              p: 2,
              bgcolor: "#F8F9FA",
              borderRadius: 2,
              maxWidth: "80%",
            }}
          >
            <Typography
              sx={{
                fontSize: 11,
                fontWeight: 600,
                color: colors.textMuted,
                mb: 0.5,
              }}
            >
              YOU • 2 HOURS AGO
            </Typography>
            <Typography
              sx={{
                fontSize: 13,
                color: colors.textPrimary,
              }}
            >
              Hi! Just checking in on your progress with the new leads. Let me know if you need any
              support.
            </Typography>
          </Box>
        </Box>

        {/* Message Input */}
        <Box sx={{ mb: 2 }}>
          <TextField
            multiline
            rows={4}
            fullWidth
            placeholder="Type your message here..."
            value={message}
            onChange={(e) => {
              if (e.target.value.length <= maxLength) {
                setMessage(e.target.value);
              }
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                fontSize: 14,
                borderRadius: 2,
                bgcolor: colors.bgPrimary,
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: colors.borderLight,
              },
            }}
          />
          <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1}>
            <Typography
              sx={{
                fontSize: 11,
                color: colors.textMuted,
              }}
            >
              {message.length}/{maxLength}
            </Typography>
          </Stack>
        </Box>

        {/* Template Options */}
        <Stack direction="row" spacing={1.5} mb={3}>
          <Button
            variant="text"
            startIcon={<FileText size={16} />}
            onClick={handleLoadTemplateFromScratch}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
              color: colors.textSecondary,
              "&:hover": {
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            Load template from Scratch
          </Button>
          <Button
            variant="text"
            startIcon={<FileText size={16} />}
            onClick={handleLoadTemplateFromFile}
            sx={{
              fontSize: 12,
              fontWeight: 600,
              textTransform: "none",
              color: colors.textSecondary,
              "&:hover": {
                bgcolor: colors.bgPrimary,
              },
            }}
          >
            Load template from File
          </Button>
        </Stack>

        {/* Action Buttons */}
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            onClick={handleSendMessage}
            disabled={!message.trim()}
            startIcon={<Send size={16} />}
            sx={{
              px: 3,
              py: 1,
              fontSize: 14,
              fontWeight: 600,
              textTransform: "none",
              bgcolor: theme.palette.primary.main,
              color: "#FFFFFF",
              borderRadius: 2,
              boxShadow: "none",
              "&:hover": {
                bgcolor: theme.palette.primary.dark,
                boxShadow: "none",
              },
              "&:disabled": {
                bgcolor: colors.border,
                color: colors.textMuted,
              },
            }}
          >
            Send
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

export default MessageAgentModal;
